/**
 * FollowerStatsSection.js
 * ========================
 * Shows per-platform follower counts, daily/weekly deltas,
 * and a 30-day multi-line growth chart with post markers.
 *
 * Used inside the Analytics Dashboard Overview tab.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Stack,
    CircularProgress, alpha, useTheme, ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import {
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip as ChartTooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { analyticsApi, importedPostsApi } from '../../services/api';

const PLATFORM_META = {
    instagram: { label: 'Instagram', Icon: InstagramIcon, color: '#E1306C' },
    youtube:   { label: 'YouTube',   Icon: YouTubeIcon,   color: '#FF0000' },
    facebook:  { label: 'Facebook',  Icon: FacebookIcon,  color: '#1877F2' },
};

const RANGE_OPTIONS = [
    { id: '7',  label: '7D',  days: 7  },
    { id: '30', label: '30D', days: 30 },
    { id: '60', label: '60D', days: 60 },
    { id: '90', label: '90D', days: 90 },
];

// ── Small helper components ───────────────────────────────────────────────────

function DeltaChip({ value, suffix = '' }) {
    if (value === undefined || value === null) return null;
    const pos = value > 0;
    const zero = value === 0;
    const color = zero ? '#94a3b8' : pos ? '#10d98a' : '#ef4444';
    const Icon  = zero ? TrendingFlatIcon : pos ? TrendingUpIcon : TrendingDownIcon;
    return (
        <Stack direction="row" alignItems="center" spacing={0.4}
            sx={{ color, fontSize: '0.72rem', fontWeight: 800 }}>
            <Icon sx={{ fontSize: 14 }} />
            <span>{pos ? '+' : ''}{value.toLocaleString()}{suffix}</span>
        </Stack>
    );
}

function PlatformFollowerCard({ platform, data, theme }) {
    const isDark = theme.palette.mode === 'dark';
    const meta   = PLATFORM_META[platform] || { label: platform, Icon: PeopleIcon, color: '#6366f1' };
    const { Icon, color, label } = meta;
    const followers = data?.followers ?? 0;
    const delta1d   = data?.delta_1d  ?? 0;
    const delta7d   = data?.delta_7d  ?? null;

    return (
        <Card sx={{
            borderRadius: '20px', height: '100%', position: 'relative', overflow: 'hidden',
            border: `1px solid ${alpha(color, isDark ? 0.2 : 0.15)}`,
            bgcolor: alpha(color, isDark ? 0.06 : 0.04),
            boxShadow: `0 4px 24px ${alpha(color, 0.12)}`,
            '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s' },
            transition: 'transform 0.2s',
        }}>
            {/* Glow orb */}
            <Box sx={{
                position: 'absolute', top: -20, right: -20, width: 100, height: 100,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(color, isDark ? 0.3 : 0.18)} 0%, transparent 70%)`,
                pointerEvents: 'none',
            }} />
            <CardContent sx={{ p: 3, position: 'relative' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                            <Icon sx={{ color, fontSize: 16 }} />
                            <Typography sx={{
                                fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.1em',
                                textTransform: 'uppercase', color: alpha(color, isDark ? 0.8 : 0.7),
                            }}>
                                {label}
                            </Typography>
                        </Stack>
                        <Typography sx={{
                            fontWeight: 900, fontSize: '2rem', lineHeight: 1,
                            letterSpacing: '-0.03em', color,
                        }}>
                            {followers.toLocaleString()}
                        </Typography>
                        <Typography sx={{
                            fontSize: '0.68rem', color: 'text.secondary', mt: 0.5, fontWeight: 600,
                        }}>
                            Followers
                        </Typography>
                    </Box>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                        bgcolor: alpha(color, isDark ? 0.18 : 0.1),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <PeopleIcon sx={{ color, fontSize: 20 }} />
                    </Box>
                </Stack>

                {/* Deltas */}
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 700, mb: 0.25 }}>TODAY</Typography>
                        <DeltaChip value={delta1d} />
                    </Box>
                    {delta7d !== null && (
                        <Box>
                            <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 700, mb: 0.25 }}>7 DAYS</Typography>
                            <DeltaChip value={delta7d} />
                        </Box>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

// Custom tooltip for the growth chart
function GrowthTooltip({ active, payload, label, postMarkers }) {
    if (!active || !payload?.length) return null;
    const posts = postMarkers?.[label] || [];
    return (
        <Box sx={{
            bgcolor: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '12px', p: 1.5, minWidth: 160,
        }}>
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', mb: 1, fontWeight: 700 }}>
                {label}
            </Typography>
            {payload.map(p => (
                <Stack key={p.dataKey} direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: p.color, fontWeight: 700 }}>
                        {PLATFORM_META[p.dataKey]?.label || p.dataKey}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#fff', fontWeight: 800 }}>
                        {(p.value || 0).toLocaleString()}
                    </Typography>
                </Stack>
            ))}
            {posts.length > 0 && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#f59e0b', fontWeight: 700, mb: 0.5 }}>
                        📌 {posts.length} post{posts.length > 1 ? 's' : ''} published
                    </Typography>
                    {posts.slice(0, 2).map((p, i) => (
                        <Typography key={i} noWrap sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', maxWidth: 180 }}>
                            {p.title || p.platform}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function FollowerStatsSection() {
    const theme  = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [loading,     setLoading]     = useState(true);
    const [followerData, setFollowerData] = useState(null);
    const [posts,       setPosts]       = useState([]);
    const [days,        setDays]        = useState('30');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [fData, pData] = await Promise.all([
                analyticsApi.getFollowers(parseInt(days)),
                importedPostsApi.getDashboard().catch(() => null),
            ]);
            setFollowerData(fData);
            setPosts(pData?.top_posts || []);
        } catch { /* silent — dashboard handles missing data gracefully */ }
        finally { setLoading(false); }
    }, [days]);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (!followerData) return null;

    const { current = {}, history = {}, total_followers = 0, total_delta_1d = 0 } = followerData;
    const platforms = Object.keys(current);

    // ── Build chart series ─────────────────────────────────────────────────
    // Merge all dates across platforms into one sorted array
    const allDates = Array.from(
        new Set(
            Object.values(history)
                .flat()
                .map(s => s.date)
        )
    ).sort();

    const chartData = allDates.map(date => {
        const row = { date: date.slice(5) }; // "MM-DD" for chart labels
        for (const [platform, snaps] of Object.entries(history)) {
            const snap = snaps.find(s => s.date === date);
            row[platform] = snap?.followers ?? null;
        }
        return row;
    });

    // Post markers: map date → [{title, platform}]
    const postMarkers = {};
    posts.forEach(p => {
        const pub = (p.published_at || '').slice(5, 10); // "MM-DD"
        if (pub) {
            if (!postMarkers[pub]) postMarkers[pub] = [];
            postMarkers[pub].push(p);
        }
    });
    const markerDates = Object.keys(postMarkers);

    // ── Total follower aggregate card ──────────────────────────────────────
    const totalDelta7d = platforms.reduce((s, p) => s + (current[p]?.delta_7d || 0), 0);

    return (
        <Box>
            {/* Section header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                        width: 34, height: 34, borderRadius: '10px',
                        bgcolor: alpha('#6366f1', isDark ? 0.15 : 0.1),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <PeopleIcon sx={{ color: '#6366f1', fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 900, fontSize: '0.95rem', lineHeight: 1.1 }}>
                            Audience & Followers
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                            Live counts · updated daily
                        </Typography>
                    </Box>
                </Stack>
                <ToggleButtonGroup
                    exclusive size="small" value={days}
                    onChange={(_, v) => v && setDays(v)}
                    sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.4, fontSize: '0.72rem', fontWeight: 700, borderRadius: '8px !important', border: `1px solid ${alpha(theme.palette.divider, 0.2)} !important` } }}
                >
                    {RANGE_OPTIONS.map(r => (
                        <ToggleButton key={r.id} value={r.id}>{r.label}</ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Stack>

            {/* Total aggregate + per-platform cards */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                {/* Total card */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{
                        borderRadius: '20px', height: '100%', position: 'relative', overflow: 'hidden',
                        border: `1px solid ${alpha('#6366f1', isDark ? 0.2 : 0.15)}`,
                        bgcolor: alpha('#6366f1', isDark ? 0.06 : 0.04),
                        boxShadow: `0 4px 24px ${alpha('#6366f1', 0.12)}`,
                    }}>
                        <Box sx={{
                            position: 'absolute', top: -20, right: -20, width: 100, height: 100,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${alpha('#6366f1', isDark ? 0.3 : 0.18)} 0%, transparent 70%)`,
                            pointerEvents: 'none',
                        }} />
                        <CardContent sx={{ p: 3, position: 'relative' }}>
                            <Typography sx={{
                                fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.1em',
                                textTransform: 'uppercase', color: alpha('#6366f1', 0.8), mb: 1,
                            }}>
                                Total Audience
                            </Typography>
                            <Typography sx={{
                                fontWeight: 900, fontSize: '2rem', lineHeight: 1,
                                letterSpacing: '-0.03em', color: '#6366f1',
                            }}>
                                {total_followers.toLocaleString()}
                            </Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.5, fontWeight: 600 }}>
                                across {platforms.length} platform{platforms.length !== 1 ? 's' : ''}
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <Box>
                                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 700, mb: 0.25 }}>TODAY</Typography>
                                    <DeltaChip value={total_delta_1d} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontWeight: 700, mb: 0.25 }}>7 DAYS</Typography>
                                    <DeltaChip value={totalDelta7d} />
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Per-platform cards */}
                {platforms.map(platform => (
                    <Grid key={platform} size={{ xs: 12, sm: 6, md: 3 }}>
                        <PlatformFollowerCard
                            platform={platform}
                            data={current[platform]}
                            theme={theme}
                        />
                    </Grid>
                ))}

                {/* Empty state if no platforms connected */}
                {platforms.length === 0 && (
                    <Grid size={{ xs: 12 }}>
                        <Box sx={{
                            py: 5, textAlign: 'center', borderRadius: '16px',
                            border: `1px dashed ${alpha(theme.palette.divider, 0.3)}`,
                        }}>
                            <PeopleIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                            <Typography color="text.secondary" fontSize="0.9rem">
                                No follower data yet — sync your posts to capture a baseline.
                            </Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>

            {/* Growth chart */}
            {chartData.length > 1 && (
                <Card sx={{
                    borderRadius: '20px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    '&:hover': { transform: 'none' },
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: '8px',
                                    bgcolor: alpha('#6366f1', 0.12),
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <TrendingUpIcon sx={{ color: '#6366f1', fontSize: 18 }} />
                                </Box>
                                <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
                                    Follower Growth — Last {days} Days
                                </Typography>
                            </Stack>
                            {/* Legend */}
                            <Stack direction="row" spacing={2}>
                                {platforms.map(p => {
                                    const meta = PLATFORM_META[p];
                                    return (
                                        <Stack key={p} direction="row" alignItems="center" spacing={0.5}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meta?.color || '#6366f1' }} />
                                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>
                                                {meta?.label || p}
                                            </Typography>
                                        </Stack>
                                    );
                                })}
                                {markerDates.length > 0 && (
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: '#f59e0b' }} />
                                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>
                                            Post published
                                        </Typography>
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>

                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke={alpha(theme.palette.divider, 0.25)}
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                                    axisLine={false}
                                    tickLine={false}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v}
                                    width={45}
                                />
                                <ChartTooltip
                                    content={<GrowthTooltip postMarkers={postMarkers} />}
                                    cursor={{ stroke: alpha(theme.palette.divider, 0.3), strokeWidth: 1 }}
                                />
                                {/* Post publish markers */}
                                {markerDates.map(date => (
                                    <ReferenceLine
                                        key={date}
                                        x={date}
                                        stroke="#f59e0b"
                                        strokeDasharray="4 2"
                                        strokeWidth={1.5}
                                        strokeOpacity={0.7}
                                    />
                                ))}
                                {/* Growth lines per platform */}
                                {platforms.map(platform => (
                                    <Line
                                        key={platform}
                                        type="monotone"
                                        dataKey={platform}
                                        stroke={PLATFORM_META[platform]?.color || '#6366f1'}
                                        strokeWidth={2.5}
                                        dot={false}
                                        activeDot={{ r: 5, strokeWidth: 0 }}
                                        connectNulls
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>

                        {markerDates.length > 0 && (
                            <Typography sx={{
                                fontSize: '0.68rem', color: 'text.secondary', mt: 1.5, textAlign: 'center',
                            }}>
                                Dashed amber lines mark when you published posts — correlate spikes with your content
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}

            {chartData.length <= 1 && platforms.length > 0 && (
                <Box sx={{
                    py: 4, textAlign: 'center', borderRadius: '16px',
                    bgcolor: alpha(theme.palette.divider, 0.04),
                    border: `1px dashed ${alpha(theme.palette.divider, 0.2)}`,
                }}>
                    <TrendingUpIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary" fontSize="0.85rem">
                        Growth chart will appear after a few days of data collection.
                    </Typography>
                    <Typography color="text.disabled" fontSize="0.75rem" sx={{ mt: 0.5 }}>
                        A background job captures your follower count daily.
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
